import { useQuery } from '@tanstack/react-query';
import { cacheArticles, clearOldCache } from '@/lib/articleCache';

export interface Article {
  title: string;
  url: string;
  source: string;
  publishedAt: string;
  description?: string;
  thumbnail?: string;
}

interface RedditPost {
  data: {
    title: string;
    url: string;
    author: string;
    created_utc: number;
    selftext: string;
    subreddit: string;
    permalink: string;
    thumbnail: string;
    preview?: {
      images: Array<{
        source: {
          url: string;
          width: number;
          height: number;
        };
      }>;
    };
  };
}

interface SubredditConfig {
  name: string;
  limit: number;
}

async function fetchRedditArticles(signal: AbortSignal): Promise<Article[]> {
  try {
    const subredditConfigs: SubredditConfig[] = [
      { name: 'worldnews', limit: 3 },
      { name: 'entertainment', limit: 20 },
      { name: 'gaming', limit: 20 },
      { name: 'nottheonion', limit: 20 },
      { name: 'InternetIsBeautiful', limit: 20 },
    ];

    const subredditPromises = subredditConfigs.map(async (config) => {
      const response = await fetch(`https://www.reddit.com/r/${config.name}/best.json?limit=${config.limit}`, { signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      return data.data.children
        .filter((post: RedditPost) => {
          try {
            const { hostname } = new URL(post.data.url);

            if (hostname === 'reddit.com') return false; // Skip Reddit's own domain
            if (hostname.endsWith('reddit.com')) return false; // Skip Reddit's own domain
            if (hostname.endsWith('.redd.it')) return false; // Skip Reddit's own media links

            if (!post.data.preview?.images?.[0]?.source?.url) {
              return false; // Skip posts without images
            }

            return true;
          } catch {
            return false;
          }
        })
        .slice(0, config.limit) // Apply the limit for this subreddit
        .map((post: RedditPost) => {
          // Get the best thumbnail image
          let thumbnail: string | undefined;

          // First try to get high-quality preview image
          if (post.data.preview?.images?.[0]?.source?.url) {
            thumbnail = post.data.preview.images[0].source.url.replace(/&amp;/g, '&');
          }
          // Fallback to Reddit's thumbnail if it's not a default one
          else if (post.data.thumbnail &&
                   post.data.thumbnail !== 'self' &&
                   post.data.thumbnail !== 'default' &&
                   post.data.thumbnail !== 'nsfw' &&
                   post.data.thumbnail.startsWith('http')) {
            thumbnail = post.data.thumbnail;
          }

          return {
            title: post.data.title,
            url: post.data.url,
            source: config.name,
            publishedAt: new Date(post.data.created_utc * 1000).toISOString(),
            thumbnail,
          };
        });
    });

    const subredditResults = await Promise.allSettled(subredditPromises);
    const allPosts = subredditResults
      .filter((result): result is PromiseFulfilledResult<Article[]> => result.status === 'fulfilled')
      .flatMap(result => result.value);

    return allPosts;
  } catch (error) {
    console.error('Error fetching Reddit articles:', error);
    return [];
  }
}

export function useRecentArticles() {
  return useQuery({
    queryKey: ['recent-articles'],
    queryFn: async (context) => {
      // Create abort signal with 2 second timeout
      const timeoutSignal = AbortSignal.timeout(2000);
      const signal = AbortSignal.any([context.signal, timeoutSignal]);

      // Only fetch from Reddit now
      const articles = await fetchRedditArticles(signal);

      // If no articles were fetched, throw an error
      if (articles.length === 0) {
        throw new Error('No articles could be fetched from any source');
      }

      // Sort by published date (most recent first)
      const sortedArticles = articles.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());

      // Cache articles in IndexedDB (don't await to avoid blocking the UI)
      cacheArticles(sortedArticles).catch(console.warn);

      // Clear old cache entries (don't await to avoid blocking the UI)
      clearOldCache().catch(console.warn);

      return sortedArticles;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
}